/**
 * TEMPORARY local-only dev API mock — DO NOT COMMIT.
 *
 * Companion to devAuth.ts. When the dev auth bypass is active (no real Stytch
 * keys), there is usually no Django backend on :8000 either, so every write/read
 * fails ("Failed to save contract"). This patches window.fetch to serve an
 * in-memory (localStorage-backed) mock for the Contracts endpoints so you can
 * exercise the UI with no backend.
 *
 * Scope: full CRUD for /contracts; seeded read data for /revenue/statements
 * (list + details tabs + mark-as-paid/delete) and /imports/vendors. Any other
 * API call returns an empty paginated response (GET) or {} (writes) so the
 * surrounding UI doesn't crash. This is a dev convenience, NOT a backend replacement.
 *
 * Installed only from seedDevAuth() (which is gated on import.meta.env.DEV +
 * placeholder keys), so it is completely inert in production builds.
 *
 * git-excluded (see .git/info/exclude). To remove: delete this file and the
 * installDevApiMock() call in devAuth.ts.
 */
import { API_BASE_URL } from "@/lib/api/core";

const STORE_KEY = "dev-mock-contracts";

function loadContracts(): any[] {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveContracts(list: any[]): void {
  localStorage.setItem(STORE_KEY, JSON.stringify(list));
}

function jsonResponse(data: unknown, status = 200): Response {
  if (status === 204) return new Response(null, { status });
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

const emptyList = { results: [], count: 0, next: null, previous: null };

/** Wrap an array as a DRF-style paginated response ({ results, count, next, previous }). */
function paginated(results: unknown[]): object {
  return { results, count: results.length, next: null, previous: null };
}

// A few sample payees so the "Add existing payees…" combobox has data and the
// "Active" status flow (which requires ≥1 payee) can be exercised.
const DEV_PAYEES = [
  {
    id: "payee-1",
    name: "Alice Producer",
    legal_name: "Alice Productions LLC",
    is_active: true,
    preferred_currency: "USD",
    country: "US",
    city: "Los Angeles",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-2",
    name: "Bob Songwriter",
    legal_name: "Bob Music Inc.",
    is_active: true,
    preferred_currency: "USD",
    country: "GB",
    city: "London",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
  {
    id: "payee-3",
    name: "Carol Mixer",
    legal_name: null,
    is_active: true,
    preferred_currency: "EUR",
    country: "DE",
    city: "Berlin",
    label: "dev-label",
    label_name: "Dev Label",
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    can_delete: true,
  },
];

function payeeName(id: string): string {
  return DEV_PAYEES.find((p) => p.id === id)?.name ?? id;
}

// --- Royalty "Create new statement" scenario contracts ---------------------
// Each contract is a complete, generatable contract (tracks + 50/50 payees) that
// drives one variant of the create-statement right panel. The scenario is named
// in the contract title so it's obvious which case is being previewed.
// `revenue_statements_preview` feeds the "Revenue statements for this period"
// table; `has_revenue`/`has_expenses` drive the amber period warnings.
function scenarioContract(opts: {
  id: string;
  name: string;
  has_revenue: boolean;
  has_expenses: boolean;
  revenue_statements_preview: { name: string; period: string; status: "settled" | "draft" }[];
}) {
  return {
    id: opts.id,
    name: opts.name,
    status: "active",
    contract_type: "exclusive_license",
    currency: "USD",
    start_date: "2024-01-01",
    end_date: null,
    territory: "Worldwide",
    statement_period: "quarterly",
    starting_balance: null,
    label: "dev-label",
    label_name: "Dev Label",
    party: "No.19 Music",
    tracks_count: 1,
    all_tracks_have_isrc: true,
    first_sound_recording_title: "Tripmastaz - While U Pretend (Original Mix)",
    can_delete: false,
    created_at: "2024-01-01T00:00:00Z",
    updated_at: "2024-01-01T00:00:00Z",
    payees: [
      {
        matched: true,
        payee: { id: "payee-adele", name: "Adele Laurie Blue Adkins" },
        share_percentage: 40,
        opening_balance: null,
        // Below-threshold balances carried from prior periods, settled in this payout.
        // The "Carried forward" total is the SUM of these (120 + 95.50 + 105 = 320.50).
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "120.00" },
          { name: "Apple Music Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "95.50" },
          { name: "YouTube Q2 2025", period: "Apr 1 - Jun 30, 2025", amount: "105.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-beyonce", name: "Beyoncé Giselle Knowles-Carter" },
        share_percentage: 35,
        opening_balance: null,
        // Single prior unpaid below-threshold statement.
        carried_statements: [
          { name: "Spotify Q3 2025", period: "Jul 1 - Sep 30, 2025", amount: "75.00" },
        ],
      },
      {
        matched: true,
        payee: { id: "payee-james", name: "James Blake Litherland" },
        share_percentage: 25,
        opening_balance: null,
        // Nothing carried forward.
        carried_statements: [],
      },
    ],
    has_revenue: opts.has_revenue,
    has_expenses: opts.has_expenses,
    revenue_statements_preview: opts.revenue_statements_preview,
    related_recordings: [],
    related_releases: [],
  };
}

const SCENARIO_PERIOD = "Oct 1, 2025 - Dec 31, 2025";

const DEV_SCENARIO_CONTRACTS = [
  scenarioContract({
    id: "roy-scn-settled",
    name: "Back in Black Agreement 1481 — Settled",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-draft",
    name: "Back in Black Agreement 1482 — Draft revenue",
    has_revenue: true,
    has_expenses: true,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "draft" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-no-revenue",
    name: "Back in Black Agreement 1483 — No revenue",
    has_revenue: false,
    has_expenses: true,
    revenue_statements_preview: [],
  }),
  scenarioContract({
    id: "roy-scn-no-expenses",
    name: "Back in Black Agreement 1484 — No expenses",
    has_revenue: true,
    has_expenses: false,
    revenue_statements_preview: [
      { name: "Spotify Q1 2026", period: SCENARIO_PERIOD, status: "settled" },
    ],
  }),
  scenarioContract({
    id: "roy-scn-none",
    name: "Back in Black Agreement 1485 — No revenue or expenses",
    has_revenue: false,
    has_expenses: false,
    revenue_statements_preview: [],
  }),
];

// --- Revenue statements mock data -----------------------------------------
// Shapes mirror the real API: SupportedVendor (/imports/vendors, a bare array)
// and RevenueStatement (/revenue/statements, paginated). Statement `vendor`
// values reference DEV_VENDORS ids so the details Metadata resolves a name.

const DEV_VENDORS = [
  { id: "vendor-above-board", name: "Above Board", default_currency: "GBP" },
  { id: "vendor-create-music-group", name: "Create Music Group", default_currency: "USD" },
  { id: "vendor-k7", name: "K7", default_currency: "EUR" },
  { id: "vendor-label-engine", name: "Label Engine", default_currency: "" },
  { id: "vendor-label-worx", name: "Label Worx", default_currency: "GBP" },
  { id: "vendor-symphonic", name: "Symphonic Distribution", default_currency: "USD" },
  { id: "vendor-orchard-legacy", name: "The Orchard (Legacy Format)", default_currency: "GBP" },
  { id: "vendor-orchard-2025", name: "The Orchard (New Version 2025)", default_currency: "GBP" },
];

// Bump the version suffix whenever the seed data shape changes so the browser
// re-seeds from DEV_REVENUE_STATEMENTS instead of using a stale localStorage copy.
const REVENUE_STORE_KEY = "dev-mock-revenue-v6";

// Placeholder invoice file so the "Download" action shows up in the UI (mock).
const DEV_INVOICE_FILE =
  "data:text/csv;charset=utf-8,Track%2CISRC%2CRevenue%0AMidnight%20Drive%2CUSRC12400001%2C3120.45";

// Seed statements: mix of vendors, currencies, statuses, and paid/unpaid so the
// list columns ("Amount received" shows values AND "-") and the "Mark as Paid"
// flow (only when date_received is empty) both render like the real app.
const DEV_REVENUE_STATEMENTS = [
  {
    id: "rev-1",
    file_name: "create_music_group_2024_q4.csv",
    import_batch: "batch-1",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-create-music-group",
    currency: "USD",
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 1284,
    revenue: "18450.32",
    created_at: "2025-01-12T09:14:00Z",
    updated_at: "2025-01-12T09:14:00Z",
    name: "Create Music Group — Q4 2024",
    can_delete: true,
    amount_received: "18120.10",
    exchange_rate: "1.0",
    date_received: "2025-02-03",
  },
  {
    id: "rev-2",
    file_name: "symphonic_2024_q4.csv",
    import_batch: "batch-2",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 642,
    revenue: "9237.78",
    created_at: "2025-01-15T11:02:00Z",
    updated_at: "2025-01-15T11:02:00Z",
    name: "Symphonic Distribution — Q4 2024",
    can_delete: true,
    amount_received: "9237.78",
    exchange_rate: "1.0",
    date_received: "2025-02-10",
  },
  {
    id: "rev-3",
    file_name: "k7_2024_q4.csv",
    import_batch: "batch-3",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-k7",
    currency: "EUR",
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 318,
    revenue: "4102.55",
    created_at: "2025-01-18T16:40:00Z",
    updated_at: "2025-01-18T16:40:00Z",
    name: "K7 — Q4 2024",
    can_delete: true,
    amount_received: null,
    exchange_rate: null,
    date_received: null,
    estimated_amount_received: "3980.00",
    estimated_date: "2026-06-01",
  },
  {
    id: "rev-4",
    file_name: "above_board_2024_q3.csv",
    import_batch: "batch-4",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-above-board",
    currency: "GBP",
    period_start: "2024-07-01",
    period_end: "2024-09-30",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 205,
    revenue: "2685.00",
    created_at: "2024-10-09T08:25:00Z",
    updated_at: "2024-10-09T08:25:00Z",
    name: "Above Board — Q3 2024",
    can_delete: true,
    amount_received: "3402.18",
    exchange_rate: "1.267",
    date_received: "2024-11-01",
    // Import-time FX estimate (label currency); actual came in higher → FX gain.
    estimated_amount_received: "3250.00",
  },
  {
    id: "rev-5",
    file_name: "create_music_group_2024_q3.csv",
    import_batch: "batch-5",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-create-music-group",
    currency: "USD",
    period_start: "2024-07-01",
    period_end: "2024-09-30",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 1190,
    revenue: "16980.44",
    created_at: "2024-10-11T10:00:00Z",
    updated_at: "2024-10-11T10:00:00Z",
    name: "Create Music Group — Q3 2024",
    can_delete: true,
    amount_received: "16980.44",
    exchange_rate: "1.0",
    date_received: "2024-11-05",
  },
  {
    id: "rev-6",
    file_name: "symphonic_2025_q1.csv",
    import_batch: "batch-6",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    file: DEV_INVOICE_FILE,
    status: "draft",
    total_lines: 412,
    revenue: "5820.15",
    created_at: "2025-04-02T13:30:00Z",
    updated_at: "2025-04-02T13:30:00Z",
    name: "Symphonic Distribution — Q1 2025",
    can_delete: true,
    amount_received: null,
    exchange_rate: null,
    date_received: null,
  },
];

// --- Revenue V2 dataset --------------------------------------------------
// The /revenue-v2 prototype models the "no currency conversion" case: every
// statement is in the label currency (USD), so there is never an FX component.
// The first three statements are FX examples (EUR / GBP) — drafts whose exchange
// rate is captured in the "Mark amount received" drawer. The remaining statements
// are USD; their differences (when present) come from bank fees / withholding.
const REVENUE_STORE_KEY_V2 = "dev-mock-revenue-v2-v4";

const DEV_REVENUE_STATEMENTS_V2 = [
  {
    id: "rev2-fx-1",
    file_name: "k7_2025_q1.csv",
    import_batch: "batch-v2-fx-1",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-k7",
    currency: "EUR",
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 372,
    revenue: "8600.00",
    created_at: "2025-04-11T09:00:00Z",
    updated_at: "2025-04-11T09:00:00Z",
    name: "K7 — Q1 2025",
    can_delete: true,
    // FX draft: reported in EUR, not yet received. Open "Mark amount received" to
    // enter the EUR → USD rate; estimate is the import-time FX value (label currency).
    amount_received: null,
    exchange_rate: null,
    date_received: null,
    estimated_amount_received: "9288.00",
    estimated_date: "2026-08-15",
  },
  {
    id: "rev2-fx-2",
    file_name: "label_worx_2025_q2.csv",
    import_batch: "batch-v2-fx-2",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-label-worx",
    currency: "GBP",
    period_start: "2025-04-01",
    period_end: "2025-06-30",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 214,
    revenue: "5400.00",
    created_at: "2025-07-10T10:00:00Z",
    updated_at: "2025-07-10T10:00:00Z",
    name: "Label Worx — Q2 2025",
    can_delete: true,
    // FX draft: reported in GBP, not yet received. Drawer captures the GBP → USD rate.
    amount_received: null,
    exchange_rate: null,
    date_received: null,
    estimated_amount_received: "6804.00",
    estimated_date: "2026-08-20",
  },
  {
    id: "rev2-fx-3",
    file_name: "k7_2024_q4.csv",
    import_batch: "batch-v2-fx-3",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-k7",
    currency: "EUR",
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 918,
    revenue: "3200.00",
    created_at: "2025-01-12T11:00:00Z",
    updated_at: "2025-01-12T11:00:00Z",
    name: "K7 — Q4 2024",
    can_delete: true,
    // FX draft: reported in EUR, not yet received. Drawer captures the EUR → USD rate.
    amount_received: null,
    exchange_rate: null,
    date_received: null,
    estimated_amount_received: "3488.00",
    estimated_date: "2026-08-10",
  },
  {
    id: "rev2-1",
    file_name: "create_music_group_2025_q1.csv",
    import_batch: "batch-v2-1",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-create-music-group",
    currency: "USD",
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 1342,
    revenue: "12480.00",
    created_at: "2025-04-10T09:14:00Z",
    updated_at: "2025-04-10T09:14:00Z",
    name: "Create Music Group — Q1 2025",
    can_delete: true,
    amount_received: "12280.00",
    exchange_rate: null,
    date_received: "2025-05-02",
  },
  {
    id: "rev2-2",
    file_name: "symphonic_2025_q1.csv",
    import_batch: "batch-v2-2",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 588,
    revenue: "8650.50",
    created_at: "2025-04-12T11:02:00Z",
    updated_at: "2025-04-12T11:02:00Z",
    name: "Symphonic Distribution — Q1 2025",
    can_delete: true,
    amount_received: "8650.50",
    exchange_rate: null,
    date_received: "2025-05-06",
  },
  {
    id: "rev2-3",
    file_name: "create_music_group_2025_q2.csv",
    import_batch: "batch-v2-3",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-create-music-group",
    currency: "USD",
    period_start: "2025-04-01",
    period_end: "2025-06-30",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 1290,
    revenue: "9320.75",
    created_at: "2025-07-09T10:00:00Z",
    updated_at: "2025-07-09T10:00:00Z",
    name: "Create Music Group — Q2 2025",
    can_delete: true,
    amount_received: null,
    exchange_rate: null,
    date_received: null,
  },
  {
    id: "rev2-4",
    file_name: "symphonic_2025_q1-b.csv",
    import_batch: "batch-v2-4",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 233,
    revenue: "15200.00",
    created_at: "2025-04-15T08:25:00Z",
    updated_at: "2025-04-15T08:25:00Z",
    name: "Symphonic Distribution — Q1 2025",
    can_delete: true,
    amount_received: "14755.00",
    exchange_rate: null,
    date_received: "2025-05-09",
  },
  {
    id: "rev2-5",
    file_name: "symphonic_2024_q4.csv",
    import_batch: "batch-v2-5",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    file: DEV_INVOICE_FILE,
    status: "processed",
    total_lines: 612,
    revenue: "6400.00",
    created_at: "2025-01-14T10:00:00Z",
    updated_at: "2025-01-14T10:00:00Z",
    name: "Symphonic Distribution — Q4 2024",
    can_delete: true,
    amount_received: "6400.00",
    exchange_rate: null,
    date_received: "2025-02-08",
  },
  {
    id: "rev2-6",
    file_name: "symphonic_2025_q2.csv",
    import_batch: "batch-v2-6",
    label: "dev-label",
    uploaded_by: "dev@local.test",
    vendor: "vendor-symphonic",
    currency: "USD",
    period_start: "2025-04-01",
    period_end: "2025-06-30",
    file: DEV_INVOICE_FILE,
    status: "ready",
    total_lines: 401,
    revenue: "4180.20",
    created_at: "2025-07-11T13:30:00Z",
    updated_at: "2025-07-11T13:30:00Z",
    name: "Symphonic Distribution — Q2 2025",
    can_delete: true,
    amount_received: null,
    exchange_rate: null,
    date_received: null,
  },
];

// The revenue dataset is chosen by route: the two prototype revenue views
// (/revenue-fx and /revenue-v2) share ONE dataset + store so they stay 1:1 —
// they differ only in the settle drawer. Any other route uses the original set.
function isV2RevenueContext(): boolean {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname;
  return path.startsWith("/revenue-fx") || path.startsWith("/revenue-v2");
}

function revenueStoreKey(): string {
  return isV2RevenueContext() ? REVENUE_STORE_KEY_V2 : REVENUE_STORE_KEY;
}

function revenueSeed(): any[] {
  return isV2RevenueContext() ? DEV_REVENUE_STATEMENTS_V2 : DEV_REVENUE_STATEMENTS;
}

function loadRevenue(): any[] {
  const key = revenueStoreKey();
  const raw = localStorage.getItem(key);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* fall through to reseed */
    }
  }
  const seed = revenueSeed();
  localStorage.setItem(key, JSON.stringify(seed));
  return seed;
}

function saveRevenue(list: any[]): void {
  localStorage.setItem(revenueStoreKey(), JSON.stringify(list));
}

// --- Corrections (audit trail) -------------------------------------------
// Stored separately from the confirmed statement record, keyed by statement id.
// Each correction is a signed delta; the effective received amount = original
// confirmed amount + sum(deltas). The original record is never overwritten.
const CORRECTIONS_STORE_KEY = "dev-mock-revenue-corrections-v1";

function loadCorrectionsMap(): Record<string, any[]> {
  try {
    return JSON.parse(localStorage.getItem(CORRECTIONS_STORE_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveCorrectionsMap(map: Record<string, any[]>): void {
  localStorage.setItem(CORRECTIONS_STORE_KEY, JSON.stringify(map));
}

function correctionsFor(id: string): any[] {
  return loadCorrectionsMap()[id] ?? [];
}

function sumCorrections(list: any[]): number {
  return list.reduce((acc, c) => acc + (parseFloat(c.amount) || 0), 0);
}

// Augment a statement response with corrections + effective received amount.
// Leaves the original record untouched: original_amount_received preserves the
// confirmed value, amount_received becomes the effective (post-correction) one.
function withCorrections(statement: any): any {
  const corrections = correctionsFor(statement.id);
  if (!statement.amount_received || corrections.length === 0) {
    return { ...statement, corrections };
  }
  const original = parseFloat(statement.amount_received) || 0;
  const effective = (original + sumCorrections(corrections)).toFixed(2);
  return {
    ...statement,
    corrections,
    original_amount_received: statement.amount_received,
    amount_received: effective,
  };
}

// --- Prototype-only dev affordances (Revenue V2) -------------------------
// Exposed so the Revenue Statements view can offer a "Reset" control and a
// "Simulate error" toggle (see RevenuePrototypeControls). Both are dev/demo
// conveniences and are inert in any build where the mock isn't installed.
//
// Reset re-seeds the in-memory dataset for the current route (V2 on /revenue-v2)
// and clears corrections, so Draft designs — and the mark-as-settled success
// toast — can be exercised again after every statement has been settled.
const SIMULATE_SETTLE_ERROR_KEY = "dev-mock-revenue-simulate-settle-error";

export function resetRevenueMock(): void {
  localStorage.setItem(revenueStoreKey(), JSON.stringify(revenueSeed()));
  localStorage.removeItem(CORRECTIONS_STORE_KEY);
}

/** When on, the next mark-as-settled PATCH is forced to fail (500). */
export function isSimulateSettleError(): boolean {
  return localStorage.getItem(SIMULATE_SETTLE_ERROR_KEY) === "true";
}

export function setSimulateSettleError(on: boolean): void {
  if (on) localStorage.setItem(SIMULATE_SETTLE_ERROR_KEY, "true");
  else localStorage.removeItem(SIMULATE_SETTLE_ERROR_KEY);
}

// --- Import batch mock ("Upload new statement" wizard) -------------------
// Auto-import path. Status advances by elapsed time so the spinners show briefly:
// analyzing → analyzed → (auto-confirm) → importing → completed.
const IMPORT_BATCHES_KEY = "dev-mock-import-batches";
const IMPORT_PHASE_MS = 2500;

// No catalog changes → can_auto_import lets the wizard skip the review step.
const EMPTY_CATALOG_PREVIEW = {
  can_import: true,
  can_auto_import: true,
  releases_to_create: [],
  releases_to_update: [],
  likely_releases: [],
  conflicts_releases: [],
  recordings_to_create: [],
  recordings_to_update: [],
  likely_recordings: [],
  conflicts_recordings: [],
  errors: { release: [], recordings: [] },
  summary: {
    new_releases: 0,
    updated_releases: 0,
    new_recordings: 0,
    updated_recordings: 0,
    likely_recordings: 0,
    likely_releases: 0,
    conflicts_recordings: 0,
    conflicts_releases: 0,
    errors: 0,
  },
};

const DEV_IMPORT_SUMMARY = {
  revenue_lines: 1284,
  releases_created: 0,
  recordings_created: 0,
  recordings_updated: 0,
  releases_updated: 0,
};

function loadImportBatches(): Record<string, any> {
  try {
    return JSON.parse(localStorage.getItem(IMPORT_BATCHES_KEY) || "{}");
  } catch {
    return {};
  }
}

function saveImportBatches(map: Record<string, any>): void {
  localStorage.setItem(IMPORT_BATCHES_KEY, JSON.stringify(map));
}

function stripInternal(batch: any): any {
  const { _phaseStart, ...rest } = batch;
  return rest;
}

function createImportBatch(body: any): any {
  const now = new Date();
  const id = crypto.randomUUID();
  const batch = {
    id,
    label: body?.label ?? "dev-label",
    name: body?.name ?? null,
    vendor: body?.vendor ?? null,
    file_name: body?.file ?? "revenue_statement.csv",
    file_size: 12480,
    status: "analyzing",
    progress: 0,
    counters: null,
    error: null,
    created_at: now.toISOString(),
    updated_at: now.toISOString(),
    _phaseStart: now.getTime(),
  };
  const map = loadImportBatches();
  map[id] = batch;
  saveImportBatches(map);
  return stripInternal(batch);
}

function pollImportBatch(id: string): any | null {
  const map = loadImportBatches();
  const batch = map[id];
  if (!batch) return null;
  const elapsed = new Date().getTime() - (batch._phaseStart ?? 0);
  if (batch.status === "analyzing" && elapsed >= IMPORT_PHASE_MS) {
    batch.status = "analyzed";
    batch.progress = 100;
    batch.updated_at = new Date().toISOString();
    batch._phaseStart = new Date().getTime();
    saveImportBatches(map);
  } else if (batch.status === "importing" && elapsed >= IMPORT_PHASE_MS) {
    batch.status = "completed";
    batch.progress = 100;
    batch.updated_at = new Date().toISOString();
    saveImportBatches(map);
  }
  return stripInternal(batch);
}

function advanceImportBatch(id: string, status: string): any | null {
  const map = loadImportBatches();
  const batch = map[id];
  if (!batch) return null;
  batch.status = status;
  batch.progress = 0;
  batch.updated_at = new Date().toISOString();
  batch._phaseStart = new Date().getTime();
  saveImportBatches(map);
  return stripInternal(batch);
}

// Per-statement detail rows. The same set is served for every statement (enough
// to populate the three details tabs); shapes match TopTrack / TrackRevenue and
// ContributorsRevenueItem.
const DEV_STATEMENT_TRACKS = [
  {
    id: "track-1",
    title: "Midnight Drive",
    isrc: "USRC12400001",
    track_number: "1",
    revenue: "3120.45",
    quantity: 412000,
    currency: "USD",
    contributors: [
      { id: "c-1", full_name: "Alice Producer", display_name: "Alice", role: "Producer" },
    ],
    release: { id: "rel-1", title: "Neon Skies", artwork: null },
  },
  {
    id: "track-2",
    title: "Paper Planes",
    isrc: "USRC12400002",
    track_number: "2",
    revenue: "2487.10",
    quantity: 331500,
    currency: "USD",
    contributors: [
      { id: "c-2", full_name: "Bob Songwriter", display_name: "Bob", role: "Songwriter" },
    ],
    release: { id: "rel-1", title: "Neon Skies", artwork: null },
  },
  {
    id: "track-3",
    title: "Coastline",
    isrc: "USRC12400003",
    track_number: "1",
    revenue: "1894.77",
    quantity: 256800,
    currency: "USD",
    contributors: [
      { id: "c-1", full_name: "Alice Producer", display_name: "Alice", role: "Producer" },
      { id: "c-3", full_name: "Carol Mixer", display_name: "Carol", role: "Mixer" },
    ],
    release: { id: "rel-2", title: "Open Water", artwork: null },
  },
  {
    id: "track-4",
    title: "Slow Burn",
    isrc: "USRC12400004",
    track_number: "3",
    revenue: "1042.00",
    quantity: 142300,
    currency: "USD",
    contributors: [
      { id: "c-2", full_name: "Bob Songwriter", display_name: "Bob", role: "Songwriter" },
    ],
    release: { id: "rel-2", title: "Open Water", artwork: null },
  },
];

const DEV_STATEMENT_CONTRIBUTORS = [
  {
    id: "contrib-rev-1",
    revenue: "5014.45",
    currency: "USD",
    quantity: 668300,
    contributors: [{ id: "c-1", name: "Alice Producer", country: "US" }],
  },
  {
    id: "contrib-rev-2",
    revenue: "3529.10",
    currency: "USD",
    quantity: 473800,
    contributors: [{ id: "c-2", name: "Bob Songwriter", country: "GB" }],
  },
  {
    id: "contrib-rev-3",
    revenue: "1894.77",
    currency: "USD",
    quantity: 256800,
    contributors: [{ id: "c-3", name: "Carol Mixer", country: "DE" }],
  },
];

// Build a fully-shaped Contract (matches the `Contract`/`ContractDetails` types)
// from a POST/PUT body, merging onto an existing record for updates.
function buildContract(body: any, existing?: any): any {
  const now = new Date().toISOString();
  const payees = Array.isArray(body?.payees)
    ? body.payees.map((p: any) => ({
        matched: true,
        payee: { id: p.payee, name: payeeName(p.payee) },
        share_percentage: p.share_percentage ?? null,
        opening_balance: p.opening_balance ?? null,
      }))
    : (existing?.payees ?? []);

  return {
    id: existing?.id ?? crypto.randomUUID(),
    created_at: existing?.created_at ?? now,
    updated_at: now,
    name: body?.name ?? existing?.name ?? "Untitled contract",
    contract_type: body?.contract_type ?? existing?.contract_type,
    status: body?.status ?? existing?.status ?? "draft",
    start_date: body?.start_date ?? existing?.start_date ?? null,
    end_date: body?.end_date ?? existing?.end_date ?? null,
    territory: body?.territory ?? existing?.territory,
    currency: body?.currency ?? existing?.currency ?? "USD",
    label: body?.label ?? existing?.label ?? "dev-label",
    label_name: "Dev Label",
    party: existing?.party ?? "",
    label_share_percent:
      body?.label_share_percent != null
        ? String(body.label_share_percent)
        : (existing?.label_share_percent ?? null),
    payment_threshold: body?.payment_threshold ?? existing?.payment_threshold ?? null,
    advance_amount: body?.advance_amount ?? existing?.advance_amount ?? null,
    statement_period: body?.statement_period ?? existing?.statement_period,
    starting_balance: existing?.starting_balance ?? null,
    payees,
    releases_count: existing?.releases_count ?? 0,
    tracks_count: existing?.tracks_count ?? 0,
    all_tracks_have_isrc: true,
    first_sound_recording_title: null,
    can_delete: true,
  };
}

// Returns a Response for handled routes, or null to fall through to real fetch.
// --- Royalty statements mock --------------------------------------------
// Reproduces the current Royalty Statements view on dummy data: list + detail
// + HTML preview + row actions (mark sent / mark paid / delete).
const ROYALTY_STORE_KEY = "dev-mock-royalty-statements-v2";

function royaltyPayeeDetails(id: string, name: string, currency: string, country: string) {
  return {
    id,
    name,
    legal_name: `${name} LLC`,
    preferred_currency: currency,
    country,
    contact_email: `${name.split(" ")[0].toLowerCase()}@example.com`,
    is_active: true,
  };
}

function royaltyContractDetails(id: string, name: string, currency: string) {
  return {
    id,
    name,
    contract_type: "recording",
    status: "active",
    start_date: "2024-01-01",
    end_date: null,
    currency,
  };
}

const DEV_ROYALTY_STATEMENTS = [
  {
    id: "roy-1",
    statement_no: "RS-2025-0001",
    payee: "payee-1",
    payee_details: royaltyPayeeDetails("payee-1", "Alice Producer", "USD", "US"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "4210.55",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-04-12T10:00:00Z",
    updated_at: "2025-04-14T10:00:00Z",
  },
  {
    id: "roy-2",
    statement_no: "RS-2025-0002",
    payee: "payee-2",
    payee_details: royaltyPayeeDetails("payee-2", "Bob Songwriter", "USD", "GB"),
    contract: "contract-2",
    contract_details: royaltyContractDetails("contract-2", "Bob — Publishing Deal", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "3180.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2025-04-12T10:05:00Z",
    updated_at: "2025-05-02T09:00:00Z",
  },
  {
    id: "roy-3",
    statement_no: "RS-2025-0003",
    payee: "payee-3",
    payee_details: royaltyPayeeDetails("payee-3", "Carol Mixer", "USD", "DE"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2025-01-01",
    period_end: "2025-03-31",
    statement_balance: "980.20",
    currency: "USD",
    file: null,
    file_url: null,
    created_by: "dev@local.test",
    status: "draft",
    created_at: "2025-04-12T10:10:00Z",
    updated_at: "2025-04-12T10:10:00Z",
  },
  {
    id: "roy-4",
    statement_no: "RS-2025-0004",
    payee: "payee-1",
    payee_details: royaltyPayeeDetails("payee-1", "Alice Producer", "USD", "US"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "5620.40",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "draft",
    created_at: "2025-01-15T10:00:00Z",
    updated_at: "2025-01-15T10:00:00Z",
  },
  {
    id: "roy-5",
    statement_no: "RS-2024-0019",
    payee: "payee-2",
    payee_details: royaltyPayeeDetails("payee-2", "Bob Songwriter", "USD", "GB"),
    contract: "contract-2",
    contract_details: royaltyContractDetails("contract-2", "Bob — Publishing Deal", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "1450.75",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "sent",
    created_at: "2025-01-15T10:05:00Z",
    updated_at: "2025-01-18T10:05:00Z",
  },
  {
    id: "roy-6",
    statement_no: "RS-2024-0020",
    payee: "payee-3",
    payee_details: royaltyPayeeDetails("payee-3", "Carol Mixer", "USD", "DE"),
    contract: "contract-1",
    contract_details: royaltyContractDetails("contract-1", "Alice — Recording Agreement", "USD"),
    period_start: "2024-10-01",
    period_end: "2024-12-31",
    statement_balance: "2310.00",
    currency: "USD",
    file: DEV_INVOICE_FILE,
    file_url: DEV_INVOICE_FILE,
    created_by: "dev@local.test",
    status: "paid",
    created_at: "2025-01-15T10:10:00Z",
    updated_at: "2025-02-01T10:10:00Z",
  },
];

function loadRoyalty(): any[] {
  const raw = localStorage.getItem(ROYALTY_STORE_KEY);
  if (raw) {
    try {
      return JSON.parse(raw);
    } catch {
      /* reseed */
    }
  }
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(DEV_ROYALTY_STATEMENTS));
  return DEV_ROYALTY_STATEMENTS;
}

function saveRoyalty(list: any[]): void {
  localStorage.setItem(ROYALTY_STORE_KEY, JSON.stringify(list));
}

function royaltyStatementHtml(s: any): string {
  return `<!doctype html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;color:#111;padding:32px;max-width:720px;margin:0 auto}
    h1{font-size:20px;margin:0 0 4px} .muted{color:#666;font-size:13px}
    table{width:100%;border-collapse:collapse;margin-top:24px;font-size:14px}
    th,td{text-align:left;padding:8px 0;border-bottom:1px solid #eee} td.r,th.r{text-align:right}
    .total{font-weight:600;font-size:16px}
  </style></head><body>
    <h1>Royalty statement ${s.statement_no}</h1>
    <div class="muted">${s.payee_details?.name ?? ""} · ${s.contract_details?.name ?? ""}</div>
    <div class="muted">Period: ${s.period_start} – ${s.period_end}</div>
    <table>
      <tr><th>Description</th><th class="r">Amount</th></tr>
      <tr><td>Net royalties for period</td><td class="r">${s.statement_balance} ${s.currency}</td></tr>
      <tr><td class="total">Amount due</td><td class="r total">${s.statement_balance} ${s.currency}</td></tr>
    </table>
  </body></html>`;
}

function route(method: string, path: string, body: any, query: URLSearchParams): Response | null {
  const trimmed = path.replace(/\/+$/, "");

  // Payees live under /contracts/payees — match BEFORE the /contracts/:id route
  // below, otherwise "payees" would be treated as a contract id.
  if (trimmed === "/contracts/payees") {
    if (method === "GET") {
      return jsonResponse({
        results: DEV_PAYEES,
        count: DEV_PAYEES.length,
        next: null,
        previous: null,
      });
    }
  }
  if (trimmed.startsWith("/contracts/payees/")) {
    const id = trimmed.slice("/contracts/payees/".length);
    const found = DEV_PAYEES.find((p) => p.id === id);
    return found ? jsonResponse(found) : jsonResponse({ detail: "Not found." }, 404);
  }

  // Supported vendors — a bare array (NOT paginated), per vendorsApi.
  if (trimmed === "/imports/vendors" && method === "GET") {
    return jsonResponse(DEV_VENDORS);
  }

  // --- Import batches (the "Upload new statement" wizard) ------------------
  // Auto-import path: analyzing → analyzed (no catalog changes → auto-confirm)
  // → importing → completed. Status advances by elapsed time on each poll.
  if (trimmed === "/imports/batches" && method === "POST") {
    return jsonResponse(createImportBatch(body), 201);
  }
  if (trimmed.startsWith("/imports/batches/")) {
    const rest = trimmed.slice("/imports/batches/".length);
    const [id, sub] = rest.split("/");

    if (sub === "catalog-preview" && method === "GET") {
      // No catalog changes → can_auto_import skips the review step.
      return jsonResponse(EMPTY_CATALOG_PREVIEW);
    }
    if (sub === "summary" && method === "GET") {
      return jsonResponse(DEV_IMPORT_SUMMARY);
    }
    if (sub === "confirm" && method === "POST") {
      const batch = advanceImportBatch(id, "importing");
      return batch ? jsonResponse({}) : jsonResponse({ detail: "Not found." }, 404);
    }
    if (!sub && method === "GET") {
      const batch = pollImportBatch(id);
      return batch ? jsonResponse(batch) : jsonResponse({ detail: "Not found." }, 404);
    }
  }

  // Revenue statements collection: /revenue/statements
  if (trimmed === "/revenue/statements") {
    if (method === "GET") {
      // Augment first so effective amount / difference can be filtered & sorted.
      let list = loadRevenue().map(withCorrections);

      // Search by name / file name (q)
      const q = query.get("q")?.toLowerCase();
      if (q) {
        list = list.filter((s) =>
          `${s.name ?? ""} ${s.file_name ?? ""}`.toLowerCase().includes(q),
        );
      }

      // Filter by enum status (legacy param: "draft,ready,…")
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((s) => allowed.has(s.status));
      }

      // Filter by derived received status: draft (no amount received) / confirmed.
      const receivedStatus = query.get("received_status");
      if (receivedStatus) {
        const allowed = new Set(receivedStatus.split(","));
        list = list.filter((s) => allowed.has(s.amount_received ? "confirmed" : "draft"));
      }

      // Filter by vendor (comma-separated ids).
      const vendorParam = query.get("vendor");
      if (vendorParam) {
        const allowed = new Set(vendorParam.split(","));
        list = list.filter((s) => allowed.has(s.vendor));
      }

      // Filter by statement period range (YYYY-MM-DD string compare is ordered).
      const periodAfter = query.get("period_start_after");
      const periodBefore = query.get("period_end_before");
      if (periodAfter) list = list.filter((s) => (s.period_start ?? "") >= periodAfter);
      if (periodBefore) list = list.filter((s) => (s.period_end ?? "") <= periodBefore);

      // Sort by ordering field; supports computed "difference" + numeric amounts.
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        const sortValue = (s: any): string => {
          if (field === "difference") {
            if (!s.amount_received) return "";
            return String((parseFloat(s.amount_received) || 0) - (parseFloat(s.revenue) || 0));
          }
          return String(s[field] ?? "");
        };
        list = [...list].sort((a, b) => {
          const cmp = sortValue(a).localeCompare(sortValue(b), undefined, { numeric: true });
          return desc ? -cmp : cmp;
        });
      }

      return jsonResponse(paginated(list));
    }
  }

  // Revenue statement detail + sub-resources: /revenue/statements/:id[/sub]
  if (trimmed.startsWith("/revenue/statements/")) {
    const rest = trimmed.slice("/revenue/statements/".length);
    const [id, sub] = rest.split("/");

    // Sub-resources powering the three details tabs (match before the item route).
    if (sub === "top-tracks" && method === "GET") return jsonResponse(paginated(DEV_STATEMENT_TRACKS));
    if (sub === "track-revenue" && method === "GET")
      return jsonResponse(paginated(DEV_STATEMENT_TRACKS));
    if (sub === "contributors-revenue" && method === "GET")
      return jsonResponse(paginated(DEV_STATEMENT_CONTRIBUTORS));

    // Corrections (audit trail): list + create.
    if (sub === "corrections") {
      if (method === "GET") return jsonResponse(paginated(correctionsFor(id)));
      if (method === "POST") {
        const list = loadRevenue();
        const statement = list.find((s) => s.id === id);
        if (!statement) return jsonResponse({ detail: "Not found." }, 404);
        const map = loadCorrectionsMap();
        const existing = map[id] ?? [];
        const original = parseFloat(statement.amount_received) || 0;
        const effective = original + sumCorrections(existing);
        const corrected = parseFloat(body?.corrected_amount);
        if (Number.isNaN(corrected)) return jsonResponse({ detail: "Invalid amount." }, 400);
        const delta = corrected - effective;
        const correction = {
          id: crypto.randomUUID(),
          amount: delta.toFixed(2),
          reason: body?.reason ?? "Other",
          description: body?.description ?? null,
          created_at: new Date().toISOString(),
        };
        map[id] = [...existing, correction];
        saveCorrectionsMap(map);
        return jsonResponse(correction, 201);
      }
    }

    // Item: /revenue/statements/:id
    if (!sub) {
      const list = loadRevenue();
      const idx = list.findIndex((s) => s.id === id);
      if (method === "GET") {
        return idx === -1
          ? jsonResponse({ detail: "Not found." }, 404)
          : jsonResponse(withCorrections(list[idx]));
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        // Prototype-only: a mark-as-settled PATCH carries amount_received. When
        // the "Simulate error" toggle is on, fail it (500) so the failed state
        // and its error toast are reachable — the prototype never fails otherwise.
        if (body?.amount_received != null && isSimulateSettleError()) {
          return jsonResponse({ detail: "Simulated failure (prototype)." }, 500);
        }
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRevenue(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRevenue(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // --- Royalty statements: list + detail + html preview + actions ---
  if (trimmed === "/revenue/royalty-statements" && method === "GET") {
    let list = loadRoyalty();
    const q = (query.get("search") || query.get("q") || "").toLowerCase();
    if (q) {
      list = list.filter((s) =>
        `${s.statement_no} ${s.payee_details?.name ?? ""} ${s.contract_details?.name ?? ""}`
          .toLowerCase()
          .includes(q),
      );
    }
    const statusParam = query.get("status");
    if (statusParam) {
      const allowed = new Set(statusParam.split(","));
      list = list.filter((s) => allowed.has(s.status));
    }
    const payeesParam = query.get("payees");
    if (payeesParam) {
      const allowed = new Set(payeesParam.split(","));
      list = list.filter((s) => allowed.has(s.payee));
    }
    const after = query.get("period_start_after");
    const before = query.get("period_end_before");
    if (after) list = list.filter((s) => (s.period_start ?? "") >= after);
    if (before) list = list.filter((s) => (s.period_end ?? "") <= before);
    const ordering = query.get("ordering");
    if (ordering) {
      const desc = ordering.startsWith("-");
      const field = desc ? ordering.slice(1) : ordering;
      list = [...list].sort((a, b) => {
        const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""), undefined, {
          numeric: true,
        });
        return desc ? -cmp : cmp;
      });
    }
    return jsonResponse(paginated(list));
  }
  if (trimmed.startsWith("/revenue/royalty-statements/")) {
    const rest = trimmed.slice("/revenue/royalty-statements/".length);
    const [id, sub] = rest.split("/");
    const list = loadRoyalty();
    const idx = list.findIndex((s) => s.id === id);

    if (sub === "html" && method === "GET") {
      return idx === -1
        ? jsonResponse({ detail: "Not found." }, 404)
        : jsonResponse({ html: royaltyStatementHtml(list[idx]) });
    }
    if (sub === "mark-sent" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "sent", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (sub === "mark-paid" && method === "POST") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      list[idx] = { ...list[idx], status: "paid", updated_at: new Date().toISOString() };
      saveRoyalty(list);
      return jsonResponse(list[idx]);
    }
    if (!sub) {
      if (method === "GET") {
        return idx === -1 ? jsonResponse({ detail: "Not found." }, 404) : jsonResponse(list[idx]);
      }
      if (method === "PATCH") {
        if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
        list[idx] = { ...list[idx], ...body, updated_at: new Date().toISOString() };
        saveRoyalty(list);
        return jsonResponse(list[idx]);
      }
      if (method === "DELETE") {
        if (idx !== -1) {
          list.splice(idx, 1);
          saveRoyalty(list);
        }
        return jsonResponse(null, 204);
      }
    }
  }

  // Collection: /contracts
  if (trimmed === "/contracts") {
    if (method === "GET") {
      let list = [...DEV_SCENARIO_CONTRACTS, ...loadContracts()];

      // Search by name (q)
      const q = query.get("q")?.toLowerCase();
      if (q) list = list.filter((c) => String(c.name ?? "").toLowerCase().includes(q));

      // Filter by status (comma-separated, e.g. "draft,active")
      const statusParam = query.get("status");
      if (statusParam) {
        const allowed = new Set(statusParam.split(","));
        list = list.filter((c) => allowed.has(c.status));
      }

      // Sort by ordering field ("status", "-status", "name", "start_date", …)
      const ordering = query.get("ordering");
      if (ordering) {
        const desc = ordering.startsWith("-");
        const field = desc ? ordering.slice(1) : ordering;
        list = [...list].sort((a, b) => {
          const cmp = String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
          return desc ? -cmp : cmp;
        });
      }

      return jsonResponse({ results: list, count: list.length, next: null, previous: null });
    }
    if (method === "POST") {
      const list = loadContracts();
      const created = buildContract(body);
      list.unshift(created);
      saveContracts(list);
      return jsonResponse(created, 201);
    }
  }

  // Item: /contracts/:id
  if (trimmed.startsWith("/contracts/")) {
    const id = trimmed.slice("/contracts/".length);

    // Scenario contracts are read-only fixtures for the create-statement panel.
    const scenario = DEV_SCENARIO_CONTRACTS.find((c) => c.id === id);
    if (scenario && method === "GET") return jsonResponse(scenario);

    const list = loadContracts();
    const idx = list.findIndex((c) => c.id === id);

    if (method === "GET") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      return jsonResponse({
        ...list[idx],
        related_recordings: [],
        related_releases: [],
        has_revenue: false,
        has_expenses: false,
      });
    }
    if (method === "PUT" || method === "PATCH") {
      if (idx === -1) return jsonResponse({ detail: "Not found." }, 404);
      const updated = buildContract(body, list[idx]);
      list[idx] = updated;
      saveContracts(list);
      return jsonResponse(updated);
    }
    if (method === "DELETE") {
      if (idx !== -1) {
        list.splice(idx, 1);
        saveContracts(list);
      }
      return jsonResponse(null, 204);
    }
  }

  // Unhandled API route: keep the UI alive with a benign empty payload.
  console.warn(`[devMock] Unhandled ${method} ${path} — returning empty response.`);
  return method === "GET" ? jsonResponse(emptyList) : jsonResponse({});
}

export function installDevApiMock(): void {
  const w = window as unknown as { __devApiMockInstalled?: boolean };
  if (w.__devApiMockInstalled) return;
  w.__devApiMockInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === "string" ? input : input instanceof Request ? input.url : String(input);

    // Only intercept calls to our API base; everything else (Vite HMR, assets) passes through.
    if (!url.startsWith(API_BASE_URL)) return originalFetch(input, init);

    const method = (
      init?.method ??
      (input instanceof Request ? input.method : "GET")
    ).toUpperCase();
    const [path, queryString = ""] = url.slice(API_BASE_URL.length).split("?");
    const query = new URLSearchParams(queryString);

    let body: any;
    const rawBody = init?.body ?? (input instanceof Request ? undefined : undefined);
    if (typeof rawBody === "string") {
      try {
        body = JSON.parse(rawBody);
      } catch {
        body = undefined;
      }
    } else if (rawBody instanceof FormData) {
      // Flatten multipart uploads (e.g. import batches) — files become their name.
      body = {};
      rawBody.forEach((value, key) => {
        body[key] = value instanceof File ? value.name : value;
      });
    }

    const handled = route(method, path, body, query);
    return handled ?? originalFetch(input, init);
  };

  console.warn("[devMock] In-browser contracts + revenue API mock active (no backend on :8000).");
}
